import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Building2, ExternalLink, Home, Menu as MenuIcon } from 'lucide-react';
import { MARKET_NAMES } from '@hc/shared';
import { PanelShell } from '../panel-shell.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import { useHome, useHomeNeighbours, useHomeSignals } from '../../api/homes-hooks.js';
import type { HomeDetail, HomeNeighbour, HomeSignal } from '../../api/client.js';
import { formatMoney } from '../../format.js';
import { useHomeRowMenu } from '../home-row-context-menu.js';
import { homeSelection } from '../use-open-home-record.js';
import { useOpenCompanyOverview } from '../use-open-company-overview.js';
import {
  basisBadge,
  cohortLabel,
  homeValueLabel,
  humanizeToken,
  pinLabel,
  termLabel,
  type BasisBadge,
} from '../homes-filters.js';
import {
  usePanelSelection,
  useSetPanelSelection,
  type PanelId,
  type SelectionKind,
  type TabId,
} from '../store.js';

// Home record — one care home from GET /homes/:homeKey (TERMINAL_HOME_GRAIN_SPEC
// step 3). The home-grain twin of Record: follows `kind: 'home'` selections on
// its channel (keeps the last home when an operator / group / landlord is
// selected), and lays the index row out in the spec's groups.
//
// RULES
//   * `{ moved_to }` alias redirects are followed (fetchHome) and noted.
//   * Fields in `not_applicable` (development rows) read "not yet operating",
//     never "—" (missing).
//   * Pin precision is Precise / Locality only — no raw geo_precision label.
//   * Every `*_basis` is a stated / statutory / modelled badge (raw value on hover).
//   * `terms_flags` (licence doubts: consume + flag) show as a provenance note.
//   * Operator links to Record; the landlord will link to the Landlords panel
//     (step 6) — the link is hidden until that panel exists.

const ACCEPTS: readonly SelectionKind[] = ['home'];
/** Flip on with the step-6 Landlords panel. */
const LANDLORDS_PANEL_ENABLED = false;

const TAB_IDS = ['overview', 'operator', 'quality', 'area', 'neighbours'] as const;
type TabKey = (typeof TAB_IDS)[number];
const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Overview',
  operator: 'Operator & property',
  quality: 'Quality & money',
  area: 'Area',
  neighbours: 'Neighbours',
};

export function HomeRecordPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const selection = usePanelSelection(tabId, panelId, ACCEPTS);
  return (
    <PanelShell tabId={tabId} panelId={panelId} padding="0">
      {selection ? (
        <Body tabId={tabId} panelId={panelId} homeKey={selection.key} />
      ) : (
        <PanelEmptyState fill icon={<Home size={18} />} title="Open a care home">
          Pick any home in <b>Homes</b> and its full record lands here — care, capacity, operator,
          property, quality, money, hazard and catchment.
          <div style={{ marginTop: 'var(--s-3)' }}>
            It follows your selection automatically. Open the{' '}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'var(--color-accent-100)',
                color: 'var(--accent)',
                borderRadius: 6,
                padding: '1px 6px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <MenuIcon size={12} style={{ flexShrink: 0 }} /> menu
            </span>{' '}
            in this panel’s header → <b>Link</b> to choose which channel it tracks.
          </div>
        </PanelEmptyState>
      )}
    </PanelShell>
  );
}

function Body({ tabId, panelId, homeKey }: { tabId: TabId; panelId: PanelId; homeKey: string }) {
  const { data, isLoading, error } = useHome(homeKey);
  const [tab, setTab] = useState<TabKey>('overview');

  if (isLoading) return <Note>Loading…</Note>;
  if (error) return <Note tone="error">Could not load this home.</Note>;
  if (!data) return null;
  const home = data.home;
  const f = fieldsOf(home);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <Header home={home} f={f} movedFrom={data.movedFrom} />
      <Tabs.Root
        value={tab}
        onValueChange={(v) => setTab(v as TabKey)}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Tabs.List
          style={{
            display: 'flex',
            gap: 'var(--s-3)',
            padding: '0 var(--s-3)',
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--surface)',
          }}
        >
          {TAB_IDS.map((k) => (
            <Tabs.Trigger
              key={k}
              value={k}
              tabIndex={tab === k ? 0 : -1}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '7px 0',
                marginBottom: '-1px',
                fontSize: 'var(--t-body)',
                cursor: 'pointer',
                color: tab === k ? 'var(--text-primary)' : 'var(--text-tertiary)',
                fontWeight: tab === k ? 600 : 500,
                borderBottom: tab === k ? '2px solid var(--accent)' : '2px solid transparent',
                whiteSpace: 'nowrap',
              }}
            >
              {TAB_LABELS[k]}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 'var(--s-3)' }}>
          <Tabs.Content value="overview">
            <OverviewTab home={home} f={f} />
          </Tabs.Content>
          <Tabs.Content value="operator">
            <OperatorTab f={f} />
          </Tabs.Content>
          <Tabs.Content value="quality">
            <QualityTab f={f} homeKey={home.identity.home_key} />
          </Tabs.Content>
          <Tabs.Content value="area">
            <AreaTab f={f} />
          </Tabs.Content>
          <Tabs.Content value="neighbours">
            {tab === 'neighbours' && (
              <Neighbours tabId={tabId} panelId={panelId} homeKey={home.identity.home_key} />
            )}
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  );
}

// --- field access ---------------------------------------------------------

/** Typed readers over the grouped row, with not-applicable awareness. */
interface Fields {
  s: (group: keyof HomeDetail, key: string) => string | null;
  n: (group: keyof HomeDetail, key: string) => number | null;
  arr: (group: keyof HomeDetail, key: string) => string[];
  na: (key: string) => boolean;
  lifecycle: string;
}

function fieldsOf(home: HomeDetail): Fields {
  const get = (g: keyof HomeDetail, k: string): unknown =>
    (home[g] as Record<string, unknown> | undefined)?.[k];
  const na = new Set(home.not_applicable ?? []);
  return {
    s: (g, k) => {
      const v = get(g, k);
      if (typeof v === 'string') return v === '' ? null : v;
      if (typeof v === 'number' || typeof v === 'boolean') return String(v);
      return null;
    },
    n: (g, k) => {
      const v = get(g, k);
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
      return null;
    },
    arr: (g, k) => {
      const v = get(g, k);
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    },
    na: (k) => na.has(k),
    lifecycle:
      typeof home.identity['lifecycle'] === 'string' ? home.identity['lifecycle'] : 'operating',
  };
}

// --- header ---------------------------------------------------------------

function Header({ home, f, movedFrom }: { home: HomeDetail; f: Fields; movedFrom: string | null }) {
  const openOperator = useOpenCompanyOverview();
  const cc = f.s('identity', 'country_code');
  const country = cc ? (MARKET_NAMES[cc as keyof typeof MARKET_NAMES] ?? cc) : null;
  const address = [
    f.s('identity', 'address_line'),
    f.s('identity', 'postcode'),
    f.s('identity', 'city'),
  ]
    .filter(Boolean)
    .join(', ');
  const operatorId = f.s('operator', 'operator_entity_id');
  const beds = f.n('capacity', 'beds');
  const terms = (home.provenance.terms_flags ?? []).filter(Boolean);

  return (
    <div
      style={{
        padding: 'var(--s-2) var(--s-3) var(--s-3)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
        {operatorId && (
          <button
            type="button"
            style={actBtnStyle}
            title="Open the operator company in Record"
            onClick={() => openOperator(operatorId)}
          >
            <Building2 size={11} /> Operator
          </button>
        )}
        {f.s('identity', 'source_url') && (
          <a
            href={f.s('identity', 'source_url')!}
            target="_blank"
            rel="noreferrer"
            style={{ ...actBtnStyle, textDecoration: 'none' }}
            title="The register entry this home comes from"
          >
            <ExternalLink size={11} /> Register
          </a>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--t-panel-title, 15px)',
            fontWeight: 700,
            color: 'var(--text-primary)',
          }}
        >
          {home.identity.name ?? '—'}
        </h2>
        {f.lifecycle === 'development' ? (
          <Chip tone="est">In development</Chip>
        ) : (
          <Chip tone="pos">Operating</Chip>
        )}
      </div>
      <div style={{ fontSize: 'var(--t-body)', color: 'var(--text-secondary)' }}>
        {[address, country].filter(Boolean).join(' · ') || '—'}
      </div>

      {/* Capacity hero — published beds only, never an estimate. */}
      <div
        style={{
          marginTop: 2,
          background: 'var(--menu-surface)',
          border: '1px solid var(--hairline)',
          borderRadius: 5,
          padding: 'var(--s-3)',
          display: 'flex',
          gap: 'var(--s-5, 24px)',
          flexWrap: 'wrap',
        }}
      >
        <Hero label="Beds">
          <Mono style={{ fontSize: 'var(--t-panel-title, 15px)', fontWeight: 700 }}>
            {beds === null ? '—' : Math.round(beds).toLocaleString('en-GB')}
          </Mono>
          <BasisChip basis={f.s('capacity', 'beds_basis')} />
        </Hero>
        <Hero label="Care">
          <span style={{ fontWeight: 600 }}>
            {[
              f.s('care', 'care_level')
                ? homeValueLabel('careLevel', f.s('care', 'care_level'))
                : null,
              f.s('care', 'setting') ? homeValueLabel('setting', f.s('care', 'setting')) : null,
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </span>
        </Hero>
        <Hero label="Operator">
          <span style={{ fontWeight: 600 }}>{f.s('operator', 'operator_name') ?? '—'}</span>
        </Hero>
      </div>

      {movedFrom && (
        <ProvenanceNote>
          This home was opened from <Mono>{movedFrom}</Mono>, which now resolves here (a development
          that opened as an operating home).
        </ProvenanceNote>
      )}
      {terms.length > 0 && (
        <ProvenanceNote>
          Licence check pending on: {terms.map(termLabel).join('; ')}. Shown as published; flagged,
          not withheld.
        </ProvenanceNote>
      )}
    </div>
  );
}

// --- tabs -----------------------------------------------------------------

function OverviewTab({ home, f }: { home: HomeDetail; f: Fields }) {
  const cohorts = f.arr('care', 'cohorts');
  const specialisms = f.arr('care', 'specialisms');
  const stays = f.arr('care', 'stay_types');
  const lat = f.n('location', 'lat');
  const lon = f.n('location', 'lon');
  const nuts = [
    f.s('location', 'region_nuts1_code'),
    f.s('location', 'region_nuts2_code'),
    f.s('location', 'region_nuts3_code'),
  ].filter(Boolean);
  return (
    <>
      <SectionTitle first>Identity</SectionTitle>
      <Row label="Register category">{text(f.s('identity', 'native_category'))}</Row>
      <Row label="Nation">{text(f.s('identity', 'nation'))}</Row>
      <Row label="Source">
        <Mono style={{ fontWeight: 400, fontSize: 'var(--t-meta)' }}>
          {f.s('identity', 'home_source') ?? '—'}:{f.s('identity', 'home_id') ?? '—'}
        </Mono>
      </Row>

      <SectionTitle>Care</SectionTitle>
      <Row label="Segment">
        <Val v={f.s('care', 'segment') ? humanizeToken(f.s('care', 'segment')!) : null} />
        <BasisChip basis={f.s('care', 'segment_basis')} />
      </Row>
      <Row label="Setting">
        <Val
          v={f.s('care', 'setting') ? homeValueLabel('setting', f.s('care', 'setting')) : null}
        />
        <BasisChip basis={f.s('care', 'setting_basis')} />
      </Row>
      <Row label="Care level">
        <Val
          v={
            f.s('care', 'care_level')
              ? homeValueLabel('careLevel', f.s('care', 'care_level'))
              : null
          }
        />
        <BasisChip basis={f.s('care', 'care_level_basis')} />
      </Row>
      <Row label="Type">
        <Val
          v={
            f.s('care', 'fine_subsegment')
              ? homeValueLabel('fineType', f.s('care', 'fine_subsegment'))
              : null
          }
        />
        <BasisChip basis={f.s('care', 'fine_subsegment_basis')} />
      </Row>
      <Row label="Who it cares for">
        <Val v={cohorts.length ? cohorts.map(cohortLabel).join(', ') : null} />
        <BasisChip basis={f.s('care', 'cohort_basis')} />
      </Row>
      <Row label="Clinical specialisms">
        <Val v={specialisms.length ? specialisms.map(cohortLabel).join(', ') : null} />
      </Row>
      <Row label="Stay">
        <Val v={stays.length ? stays.map((s) => homeValueLabel('stay', s)).join(' + ') : null} />
        <BasisChip basis={f.s('care', 'stay_basis')} />
      </Row>

      <SectionTitle>Location</SectionTitle>
      <Row label="Pin">
        <Val v={pinLabel(f.s('location', 'pin_level'))} />
      </Row>
      <Row label="Coordinates">
        {lat !== null && lon !== null ? (
          <Mono>
            {lat.toFixed(5)}, {lon.toFixed(5)}
          </Mono>
        ) : (
          <Dash />
        )}
      </Row>
      <Row label="NUTS 1 · 2 · 3">
        <Val v={nuts.length ? nuts.join(' · ') : null} mono />
      </Row>
      <Row label="Municipality">{text(f.s('location', 'lau_name'))}</Row>
      <Row label="Urbanicity">
        <Val
          v={f.s('location', 'urbanicity') ? humanizeToken(f.s('location', 'urbanicity')!) : null}
        />
      </Row>

      <SectionTitle>Capacity</SectionTitle>
      <Row label="Beds">
        <NumVal n={f.n('capacity', 'beds')} suffix={unitSuffix(f.s('capacity', 'beds_unit'))} />
        <YearMeta y={f.s('capacity', 'beds_year')} />
        <BasisChip basis={f.s('capacity', 'beds_basis')} />
      </Row>
      <Row label="Rooms">
        <NumVal n={f.n('capacity', 'rooms')} />
        {f.n('capacity', 'rooms_double') !== null && (
          <Meta>{f.n('capacity', 'rooms_double')} double</Meta>
        )}
      </Row>
      <Row label="Official places">
        <NumVal n={f.n('capacity', 'official_places')} />
        {f.s('capacity', 'official_place_type') && (
          <Meta>{humanizeToken(f.s('capacity', 'official_place_type')!)}</Meta>
        )}
        <YearMeta y={f.s('capacity', 'official_places_year')} />
      </Row>
      <Row label="Occupancy">
        {f.na('occupancy_value') ? (
          <NotYet />
        ) : (
          <>
            <NumVal
              n={f.n('capacity', 'occupancy_value')}
              suffix={unitSuffix(f.s('capacity', 'occupancy_unit'))}
            />
            <YearMeta y={f.s('capacity', 'occupancy_year')} />
            <BasisChip basis={f.s('capacity', 'occupancy_basis')} />
          </>
        )}
      </Row>

      {f.lifecycle === 'development' && <DevelopmentSection f={f} />}

      <SectionTitle>Provenance</SectionTitle>
      <Row label="Home key">
        <Mono style={{ fontWeight: 400, fontSize: 'var(--t-meta)', wordBreak: 'break-all' }}>
          {home.identity.home_key}
        </Mono>
      </Row>
      <Row label="First loaded">{text(dateOnly(f.s('provenance', 'first_loaded_at')))}</Row>
      <Row label="Last seen on register">{text(dateOnly(f.s('provenance', 'last_seen_at')))}</Row>
      <Row label="Licence flags">
        {(home.provenance.terms_flags ?? []).length ? (
          <span style={{ fontWeight: 400 }}>
            {(home.provenance.terms_flags ?? []).map(termLabel).join('; ')}
          </span>
        ) : (
          <Meta>none</Meta>
        )}
      </Row>
    </>
  );
}

function DevelopmentSection({ f }: { f: Fields }) {
  const from = f.s('development', 'cod_from');
  const to = f.s('development', 'cod_to');
  return (
    <>
      <SectionTitle>Development</SectionTitle>
      <Row label="Stage">
        <Val v={f.s('development', 'stage') ? humanizeToken(f.s('development', 'stage')!) : null} />
        {f.s('development', 'stage_date') && <Meta>{f.s('development', 'stage_date')}</Meta>}
      </Row>
      <Row label="Expected opening">
        <Val v={from || to ? `${from ?? '?'} – ${to ?? '?'}` : null} />
        <BasisChip basis={f.s('development', 'cod_basis')} />
      </Row>
      <Row label="Applicant">{text(f.s('development', 'applicant_name'))}</Row>
      <Row label="Developer">{text(f.s('development', 'developer_group'))}</Row>
    </>
  );
}

function OperatorTab({ f }: { f: Fields }) {
  const openOperator = useOpenCompanyOverview();
  const operatorId = f.s('operator', 'operator_entity_id');
  const linked = f.s('operator', 'operator_linked') === 'true';
  const turnover = f.n('operator', 'operator_turnover_eur');
  const turnoverBasis = f.s('operator', 'operator_turnover_basis');
  const landlord = f.s('property', 'landlord_group');
  const landlordKey = f.s('property', 'landlord_key');
  const price = f.n('property', 'last_price_paid');
  const m2 = f.n('property', 'building_m2');
  return (
    <>
      <SectionTitle first>Operator</SectionTitle>
      <Row label="Operator">
        {operatorId ? (
          <button type="button" style={linkStyle} onClick={() => openOperator(operatorId)}>
            {f.s('operator', 'operator_name') ?? 'Open operator'}
          </button>
        ) : (
          <Val v={f.s('operator', 'operator_name')} />
        )}
      </Row>
      {!linked && f.s('operator', 'operator_name') && (
        <Row label="">
          <Meta>The register’s published operator — no linked company in care scope.</Meta>
        </Row>
      )}
      <Row label="Sector">
        <Val
          v={
            f.s('operator', 'operator_sector')
              ? homeValueLabel('operatorSector', f.s('operator', 'operator_sector'))
              : null
          }
        />
        {f.s('operator', 'operator_public_kind') && (
          <Meta>{humanizeToken(f.s('operator', 'operator_public_kind')!)}</Meta>
        )}
      </Row>
      <Row label="Group">{text(f.s('operator', 'economic_group'))}</Row>
      <Row label="Corporate parent">{text(f.s('operator', 'corporate_parent'))}</Row>
      <Row label="Ownership type">{text(f.s('operator', 'ownership_type'))}</Row>
      <Row label="Operator homes">
        <NumVal n={f.n('operator', 'operator_homes')} />
      </Row>
      <Row label="Operator turnover (all homes)">
        {turnover !== null ? <Mono>{formatMoney(turnover, { currency: '€' })}</Mono> : <Dash />}
        <BasisChip basis={turnoverBasis} />
      </Row>

      <SectionTitle>Property</SectionTitle>
      <Row label="Tenure">
        <Val
          v={f.s('property', 'tenure') ? homeValueLabel('tenure', f.s('property', 'tenure')) : null}
        />
      </Row>
      <Row label="Landlord">
        {landlord ? (
          LANDLORDS_PANEL_ENABLED && landlordKey ? (
            <button type="button" style={linkStyle} title={landlordKey}>
              {landlord}
            </button>
          ) : (
            <span>{landlord}</span>
          )
        ) : (
          <Dash />
        )}
        {f.s('property', 'landlord_type') && (
          <Meta>{homeValueLabel('landlordType', f.s('property', 'landlord_type'))}</Meta>
        )}
        {f.s('property', 'landlord_country') && <Meta>{f.s('property', 'landlord_country')}</Meta>}
      </Row>
      <Row label="Named in landlord portfolio">
        <Val
          v={
            f.s('property', 'named_in_landlord_portfolio') === null
              ? null
              : f.s('property', 'named_in_landlord_portfolio') === 'true'
                ? 'Yes'
                : 'No'
          }
        />
      </Row>
      <Row label="Legal owner">
        <Val v={f.s('property', 'legal_owner_name')} />
        {f.s('property', 'legal_owner_type') && (
          <Meta>{humanizeToken(f.s('property', 'legal_owner_type')!)}</Meta>
        )}
      </Row>
      <Row label="Title · UPRN · building ref">
        <Val
          v={
            [f.s('property', 'title_ref'), f.s('property', 'uprn'), f.s('property', 'building_ref')]
              .filter(Boolean)
              .join(' · ') || null
          }
          mono
        />
      </Row>
      <Row label="Building">
        <Val
          v={
            [
              m2 !== null ? `${Math.round(m2).toLocaleString('en-GB')} m²` : null,
              f.s('property', 'building_year_built')
                ? `built ${f.s('property', 'building_year_built')}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ') || null
          }
        />
      </Row>
      <Row label="Last price paid">
        {price !== null ? <Mono>{formatMoney(price, { currency: '£' })}</Mono> : <Dash />}
        {f.s('property', 'last_price_date') && <Meta>{f.s('property', 'last_price_date')}</Meta>}
      </Row>
    </>
  );
}

function QualityTab({ f, homeKey }: { f: Fields; homeKey: string }) {
  const rating = f.s('quality', 'rating');
  const ratingUrl = f.s('quality', 'rating_url');
  const fee = f.n('money', 'fee_value');
  const revenue = f.n('money', 'reported_revenue');
  return (
    <>
      <SectionTitle first>Quality</SectionTitle>
      <Row label="Rating">
        {f.na('rating') ? (
          <NotYet />
        ) : rating ? (
          ratingUrl ? (
            <a href={ratingUrl} target="_blank" rel="noreferrer" style={linkStyle}>
              {humanizeToken(rating)}
            </a>
          ) : (
            <span>{humanizeToken(rating)}</span>
          )
        ) : (
          <Dash />
        )}
      </Row>
      {f.s('quality', 'rating_scale') && (
        <Row label="Scale">
          <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>
            {f.s('quality', 'rating_scale')}
          </span>
        </Row>
      )}
      <Row label="Rated on">
        <Val v={f.s('quality', 'rating_date')} />
        {f.s('quality', 'rating_valid_until') && (
          <Meta>valid until {f.s('quality', 'rating_valid_until')}</Meta>
        )}
        {f.s('quality', 'rating_kind') && (
          <Meta>{humanizeToken(f.s('quality', 'rating_kind')!)}</Meta>
        )}
      </Row>
      <Row label="Rated inspections">
        {f.na('rated_inspections') ? (
          <NotYet />
        ) : (
          <NumVal n={f.n('quality', 'rated_inspections')} />
        )}
      </Row>
      <Row label="Latest enforcement">
        {f.na('enforcement_latest_type') ? (
          <NotYet />
        ) : (
          <>
            <Val
              v={
                f.s('quality', 'enforcement_latest_type')
                  ? humanizeToken(f.s('quality', 'enforcement_latest_type')!)
                  : null
              }
            />
            {f.s('quality', 'enforcement_latest_date') && (
              <Meta>{f.s('quality', 'enforcement_latest_date')}</Meta>
            )}
          </>
        )}
      </Row>
      <Row label="Care-regulator signals">
        <SignalVerdictChip v={f.s('quality', 'quality_signal_verdict')} />
      </Row>
      <Row label="Other-source signals">
        <SignalVerdictChip v={f.s('quality', 'alt_signal_verdict')} />
      </Row>
      <SignalList homeKey={homeKey} />

      <SectionTitle>Money</SectionTitle>
      <Row label="Public funding">
        <Val
          v={
            f.s('money', 'public_funding')
              ? homeValueLabel('publicFunding', f.s('money', 'public_funding'))
              : null
          }
        />
        <BasisChip basis={f.s('money', 'public_funding_basis')} />
      </Row>
      <Row label="Fee">
        {fee !== null ? (
          <Mono>
            {fee.toLocaleString('en-GB', { maximumFractionDigits: 2 })}
            {f.s('money', 'fee_unit') ? ` ${f.s('money', 'fee_unit')}` : ''}
          </Mono>
        ) : (
          <Dash />
        )}
        {f.s('money', 'fee_date') && <Meta>{f.s('money', 'fee_date')}</Meta>}
        <BasisChip basis={f.s('money', 'fee_basis')} />
      </Row>
      <Row label="Reported revenue (this home)">
        {revenue !== null ? (
          <Mono>
            {formatMoney(revenue)} {f.s('money', 'revenue_currency') ?? ''}
          </Mono>
        ) : (
          <Dash />
        )}
        <YearMeta y={f.s('money', 'revenue_year')} />
      </Row>
    </>
  );
}

function AreaTab({ f }: { f: Fields }) {
  const radius = f.n('catchment', 'catchment_radius_km');
  const g65 = f.n('catchment', 'growth_65_pct');
  const g80 = f.n('catchment', 'growth_80_pct');
  const labourBasis = f.s('workforce', 'labour_basis');
  const pay = f.n('workforce', 'hourly_pay');
  return (
    <>
      <SectionTitle first>Hazard</SectionTitle>
      <HazardRow
        label="Flood depth (1-in-100 yr)"
        value={f.n('hazard', 'flood_depth_rp100_m')}
        unit=" m"
        verdict={f.s('hazard', 'flood_verdict')}
      />
      <HazardRow
        label="Hot days > 30 °C"
        value={f.n('hazard', 'hot_days_30c')}
        unit=" /yr"
        verdict={f.s('hazard', 'heat_verdict')}
      />
      <HazardRow
        label="Fire-danger days"
        value={f.n('hazard', 'fire_danger_days')}
        unit=" /yr"
        verdict={f.s('hazard', 'wildfire_verdict')}
      />
      <Row label="Coastal erosion">
        <Val v={f.s('hazard', 'coastal_erosion')} />
        <VerdictChip v={f.s('hazard', 'coastal_verdict')} />
      </Row>
      <HazardRow
        label="Ground movement"
        value={f.n('hazard', 'subsidence_mm_yr')}
        unit=" mm/yr"
        verdict={f.s('hazard', 'subsidence_verdict')}
      />

      <SectionTitle>Catchment{radius !== null ? ` (${radius} km)` : ''}</SectionTitle>
      <Row label="Population">
        <NumVal n={f.n('catchment', 'catchment_pop')} />
      </Row>
      <Row label="Elderly population">
        <NumVal n={f.n('catchment', 'elderly_pop')} />
        {f.s('catchment', 'elderly_band') && <Meta>{f.s('catchment', 'elderly_band')}</Meta>}
      </Row>
      <Row label="Income percentile">
        <NumVal n={f.n('catchment', 'income_pctl')} decimals={0} />
        {f.s('catchment', 'income_grain') && (
          <Meta>{humanizeToken(f.s('catchment', 'income_grain')!).toLowerCase()} grain</Meta>
        )}
      </Row>
      <Row label="Growth 65+ · 80+">
        <Val
          v={g65 !== null || g80 !== null ? `${signedPct(g65)} · ${signedPct(g80)}` : null}
          mono
        />
      </Row>
      <Row label="Competing homes · beds">
        <Val
          v={
            f.n('catchment', 'competing_homes') !== null ||
            f.n('catchment', 'competing_beds') !== null
              ? `${fmtInt(f.n('catchment', 'competing_homes'))} · ${fmtInt(f.n('catchment', 'competing_beds'))}`
              : null
          }
          mono
        />
      </Row>
      <Row label="Places in catchment">
        <NumVal n={f.n('catchment', 'catchment_places')} />
      </Row>
      <Row label="Places per 1,000 elderly">
        <NumVal n={f.n('catchment', 'places_per_1000_elderly')} decimals={1} />
      </Row>
      <Row label="Payer pressure">
        <Val
          v={
            f.s('catchment', 'payer_pressure')
              ? homeValueLabel('payerPressure', f.s('catchment', 'payer_pressure'))
              : null
          }
        />
        {f.arr('catchment', 'payer_pressure_kinds').length > 0 && (
          <Meta>{f.arr('catchment', 'payer_pressure_kinds').map(humanizeToken).join(', ')}</Meta>
        )}
      </Row>

      <SectionTitle>Workforce</SectionTitle>
      <Row label="Care vacancy rate">
        {f.na('vacancy_rate_pct') ? (
          <NotYet />
        ) : (
          <NumVal n={f.n('workforce', 'vacancy_rate_pct')} decimals={1} suffix="%" />
        )}
      </Row>
      <Row label="Staff turnover">
        {f.na('turnover_rate_pct') ? (
          <NotYet />
        ) : (
          <NumVal n={f.n('workforce', 'turnover_rate_pct')} decimals={1} suffix="%" />
        )}
      </Row>
      <Row label="Unemployment">
        <NumVal n={f.n('workforce', 'unemployment_rate_pct')} decimals={1} suffix="%" />
      </Row>
      <Row label="Labour basis">
        <Val v={labourBasis ? (LABOUR_BASIS[labourBasis] ?? humanizeToken(labourBasis)) : null} />
        <BasisChip basis={labourBasis} />
      </Row>
      <Row label="Hourly pay">
        {f.na('hourly_pay') ? (
          <NotYet />
        ) : pay !== null ? (
          <Mono>
            {pay.toFixed(2)} {f.s('workforce', 'hourly_pay_currency') ?? ''}
          </Mono>
        ) : (
          <Dash />
        )}
      </Row>
      <Row label="Staff (stated)">
        {f.na('staff_stated') ? (
          <NotYet />
        ) : (
          <>
            <NumVal n={f.n('workforce', 'staff_stated')} />
            {f.s('workforce', 'staff_unit') && <Meta>{f.s('workforce', 'staff_unit')}</Meta>}
          </>
        )}
      </Row>
    </>
  );
}

// --- quality signals --------------------------------------------------------

const SIGNAL_VERDICT_LABEL: Record<string, string> = {
  positive: 'Positive',
  negative: 'Negative',
  mixed: 'Mixed',
  neutral: 'Neutral only',
};

/** The index's signal verdict (0296) — a SIGNAL summary, never a grade. */
function SignalVerdictChip({ v }: { v: string | null }) {
  if (!v) return <Dash />;
  const tone: ChipTone =
    v === 'positive' ? 'pos' : v === 'negative' ? 'neg' : v === 'mixed' ? 'est' : 'neutral';
  return <Chip tone={tone}>{SIGNAL_VERDICT_LABEL[v] ?? humanizeToken(v)}</Chip>;
}

const SIGNALS_SHOWN = 8;

/** Every current quality signal of the home, verbatim with its publisher: the care regulator's own first, then
 *  other sources (food hygiene, staffing statistics …). Negative first within each group. */
function SignalList({ homeKey }: { homeKey: string }) {
  const { data, isLoading, error } = useHomeSignals(homeKey);
  if (isLoading) return <Note>Loading signals…</Note>;
  if (error) return <Note tone="error">Could not load signals.</Note>;
  const all = data?.signals ?? [];
  if (all.length === 0) return null;
  const regulator = all.filter((x) => x.signal_class !== 'alternative');
  const other = all.filter((x) => x.signal_class === 'alternative');
  return (
    <>
      {regulator.length > 0 && <SignalGroup title="Care-regulator signals" items={regulator} />}
      {other.length > 0 && <SignalGroup title="Other-source signals" items={other} />}
    </>
  );
}

function SignalGroup({ title, items }: { title: string; items: HomeSignal[] }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, SIGNALS_SHOWN);
  return (
    <div style={{ marginTop: 'var(--s-3)' }}>
      <div
        style={{
          fontSize: 'var(--t-meta)',
          fontWeight: 600,
          color: 'var(--text-secondary)',
          paddingBottom: 2,
        }}
      >
        {title} <Meta>· {items.length}</Meta>
      </div>
      {shown.map((x, i) => (
        <SignalItem key={`${x.source}:${x.signal}:${i}`} x={x} />
      ))}
      {items.length > SIGNALS_SHOWN && (
        <button type="button" style={{ ...linkStyle, marginTop: 4 }} onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}

const CLASS_LABEL: Record<string, string> = {
  accreditation: 'Accreditation',
  enforcement: 'Enforcement',
  enforcement_clear: 'Enforcement lifted',
  inspection: 'Inspection',
  registration: 'Registration',
  indicator: 'Indicator',
  alternative: 'Other source',
};

function SignalItem({ x }: { x: HomeSignal }) {
  const tone: ChipTone =
    x.direction === 'positive' ? 'pos' : x.direction === 'negative' ? 'neg' : 'neutral';
  const date = x.effective_from ?? x.observed_on;
  return (
    <div
      style={{
        padding: '6px 0',
        borderBottom: '1px solid var(--hairline)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontSize: 'var(--t-body)',
      }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Chip tone={tone}>{CLASS_LABEL[x.signal_class] ?? humanizeToken(x.signal_class)}</Chip>
        <span style={{ color: 'var(--text-primary)' }}>{x.statement}</span>
      </div>
      <Meta>
        {x.publisher}
        {date ? ` · ${date.slice(0, 10)}` : ''}
        {x.effective_to ? ` – ${x.effective_to.slice(0, 10)}` : ''}
        {x.terms_status ? ` · licence: ${humanizeToken(x.terms_status).toLowerCase()}` : ''}
        {x.source_url && (
          <>
            {' · '}
            <a
              href={x.source_url}
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--accent)' }}
            >
              source
            </a>
          </>
        )}
      </Meta>
    </div>
  );
}

const LABOUR_BASIS: Record<string, string> = {
  gb_catchment: 'GB catchment rate',
  nuts_proxy: 'Regional proxy (NUTS)',
  own_home_region: 'Regional rate (own-home place)',
};

// --- neighbours -----------------------------------------------------------

const RADII = [2, 5, 10, 25] as const;

function Neighbours({
  tabId,
  panelId,
  homeKey,
}: {
  tabId: TabId;
  panelId: PanelId;
  homeKey: string;
}) {
  const [radius, setRadius] = useState<number>(5);
  const { data, isLoading, error } = useHomeNeighbours(homeKey, radius);
  const setPanelSelection = useSetPanelSelection(tabId, panelId);
  const rows = data?.neighbours ?? [];
  const beds = rows.reduce((s, r) => s + (r.beds ?? 0), 0);
  const operators = new Set(rows.map((r) => r.operator_entity_id).filter(Boolean)).size;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--text-secondary)' }}>Within</span>
        {RADII.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRadius(r)}
            style={{
              ...actBtnStyle,
              ...(radius === r
                ? { background: 'var(--color-accent-100)', color: 'var(--accent)' }
                : {}),
            }}
          >
            {r} km
          </button>
        ))}
        <span style={{ flex: 1 }} />
        {data && (
          <Meta>
            {rows.length.toLocaleString('en-GB')}
            {rows.length >= 100 ? '+' : ''} homes · {beds.toLocaleString('en-GB')} published beds ·{' '}
            {operators.toLocaleString('en-GB')} operators
          </Meta>
        )}
      </div>
      {isLoading ? (
        <Note>Loading…</Note>
      ) : error ? (
        <Note tone="error">Could not load neighbours.</Note>
      ) : rows.length === 0 ? (
        <Note>No other homes within {radius} km (or this home has no map pin).</Note>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--t-body)' }}>
          <thead>
            <tr>
              <th style={th('left')}>Home</th>
              <th style={th('right')}>km</th>
              <th style={th('right')}>Beds</th>
              <th style={th('left')}>Care</th>
              <th style={th('left')}>Operator</th>
              <th style={th('left')}>Rating</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => (
              <NeighbourRow
                key={n.home_key}
                n={n}
                onSelect={() =>
                  setPanelSelection(
                    homeSelection({
                      homeKey: n.home_key,
                      name: n.name,
                      operatorEntityId: n.operator_entity_id,
                    }),
                  )
                }
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NeighbourRow({ n, onSelect }: { n: HomeNeighbour; onSelect: () => void }) {
  const [hover, setHover] = useState(false);
  const { onContextMenu, menu } = useHomeRowMenu({
    homeKey: n.home_key,
    name: n.name,
    operatorEntityId: n.operator_entity_id,
  });
  return (
    <>
      <tr
        onClick={onSelect}
        onContextMenu={onContextMenu}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        title="Click to open this home here · right-click for more"
        style={{ cursor: 'pointer', background: hover ? 'var(--hover-subtle)' : 'transparent' }}
      >
        <td style={td('left')}>
          <span style={{ fontWeight: 600 }}>{n.name ?? '—'}</span>
          {n.pin_level === 'locality' && <Meta> · locality pin</Meta>}
        </td>
        <td style={{ ...td('right'), fontFamily: 'var(--font-mono)' }}>
          {n.distance_km.toFixed(1)}
        </td>
        <td style={{ ...td('right'), fontFamily: 'var(--font-mono)' }}>
          {n.beds === null ? '—' : Math.round(n.beds)}
        </td>
        <td style={td('left')}>{n.care_level ? homeValueLabel('careLevel', n.care_level) : '—'}</td>
        <td style={td('left')}>{n.operator_name ?? '—'}</td>
        <td style={td('left')} title={n.rating_scale ?? undefined}>
          {n.rating ? humanizeToken(n.rating) : '—'}
        </td>
      </tr>
      {menu}
    </>
  );
}

// --- primitives (same recipe as Record) ------------------------------------

function SectionTitle({ children, first }: { children: React.ReactNode; first?: boolean }) {
  return (
    <div
      style={{
        marginTop: first ? 0 : 'var(--s-4)',
        paddingBottom: 4,
        fontSize: 'var(--t-meta)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 600,
        color: 'var(--text-tertiary)',
        borderBottom: '1px solid var(--hairline)',
      }}
    >
      {children}
    </div>
  );
}

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 'var(--s-3)',
        padding: '5px 0',
        borderBottom: '1px solid var(--hairline)',
        fontSize: 'var(--t-body)',
      }}
    >
      <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</span>
      <span
        style={{
          textAlign: 'right',
          fontWeight: 600,
          display: 'flex',
          gap: 6,
          alignItems: 'baseline',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        {children}
      </span>
    </div>
  );
}

function Hero({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontSize: 'var(--t-meta)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-tertiary)',
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

function HazardRow({
  label,
  value,
  unit,
  verdict,
}: {
  label: string;
  value: number | null;
  unit: string;
  verdict: string | null;
}) {
  return (
    <Row label={label}>
      {value !== null ? (
        <Mono>
          {value.toLocaleString('en-GB', { maximumFractionDigits: 2 })}
          {unit}
        </Mono>
      ) : (
        <Dash />
      )}
      <VerdictChip v={verdict} />
    </Row>
  );
}

function VerdictChip({ v }: { v: string | null }) {
  if (!v) return null;
  const tone: ChipTone = v === 'material' ? 'neg' : v === 'low' ? 'est' : 'neutral';
  return <Chip tone={tone}>{humanizeToken(v)}</Chip>;
}

const BADGE_TONE: Record<BasisBadge, ChipTone> = {
  stated: 'neutral',
  statutory: 'info',
  modelled: 'est',
};

/** stated / statutory / modelled badge for a `*_basis`; raw value on hover. */
function BasisChip({ basis }: { basis: string | null }) {
  const badge = basisBadge(basis);
  if (!badge) return null;
  return (
    <span title={`Basis: ${basis}`}>
      <Chip tone={BADGE_TONE[badge]}>{badge}</Chip>
    </span>
  );
}

function ProvenanceNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="note"
      style={{
        fontSize: 'var(--t-meta)',
        color: 'var(--text-secondary)',
        background: 'var(--menu-surface)',
        borderLeft: '2px solid var(--accent)',
        padding: '4px var(--s-2)',
      }}
    >
      {children}
    </div>
  );
}

function Val({ v, mono }: { v: string | null; mono?: boolean }) {
  if (v === null || v === '') return <Dash />;
  return mono ? <Mono>{v}</Mono> : <span>{v}</span>;
}

function NumVal({
  n,
  decimals = 0,
  suffix = '',
}: {
  n: number | null;
  decimals?: number;
  suffix?: string;
}) {
  if (n === null) return <Dash />;
  return (
    <Mono>
      {n.toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </Mono>
  );
}

function YearMeta({ y }: { y: string | null }) {
  return y ? <Meta>{y}</Meta> : null;
}

function NotYet() {
  return (
    <span style={{ fontWeight: 400, fontStyle: 'italic', color: 'var(--text-tertiary)' }}>
      not yet operating
    </span>
  );
}

function Dash() {
  return <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>—</span>;
}

function text(v: string | null) {
  return v ? <span>{v}</span> : <Dash />;
}

function Meta({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontWeight: 400, color: 'var(--text-tertiary)', fontSize: 'var(--t-meta)' }}>
      {children}
    </span>
  );
}

function Mono({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', ...style }}>
      {children}
    </span>
  );
}

function Note({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div
      style={{
        padding: 'var(--s-3)',
        color: tone === 'error' ? 'var(--color-negative-500)' : 'var(--text-tertiary)',
      }}
    >
      {children}
    </div>
  );
}

type ChipTone = 'pos' | 'neg' | 'est' | 'info' | 'neutral';

function Chip({ tone, children }: { tone: ChipTone; children: React.ReactNode }) {
  const palette: Record<ChipTone, { bg: string; fg: string; border: string }> = {
    pos: { bg: 'var(--positive-wash, #e4f0ea)', fg: 'var(--positive, #0a7d5a)', border: '#b7d4af' },
    neg: { bg: 'var(--negative-wash, #f7e3e8)', fg: 'var(--negative, #c0143c)', border: '#e3b8c2' },
    est: { bg: '#f4e3c4', fg: 'var(--accent-hover, #8f4822)', border: '#e0c79a' },
    info: {
      bg: 'var(--surface-elevated, #e9d4b6)',
      fg: 'var(--accent, #a8572a)',
      border: '#d8c19c',
    },
    neutral: { bg: 'var(--menu-surface)', fg: 'var(--text-primary)', border: 'var(--hairline)' },
  };
  const p = palette[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 'var(--t-meta)',
        padding: '1px 6px',
        border: `1px solid ${p.border}`,
        borderRadius: 3,
        background: p.bg,
        color: p.fg,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

const actBtnStyle: React.CSSProperties = {
  height: 22,
  border: '1px solid var(--hairline)',
  background: 'var(--menu-surface)',
  color: 'var(--text-secondary)',
  borderRadius: 4,
  fontSize: 'var(--t-meta)',
  fontWeight: 600,
  padding: '0 8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
};

const linkStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'var(--accent)',
  fontWeight: 600,
  fontSize: 'inherit',
  textAlign: 'right',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

function th(align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    padding: '4px 6px',
    fontSize: 'var(--t-meta)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'var(--text-secondary)',
    borderBottom: '2px solid var(--text-primary)',
    background: 'var(--surface-elevated)',
    whiteSpace: 'nowrap',
  };
}

function td(align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    padding: '4px 6px',
    borderBottom: '1px solid var(--border-subtle)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 200,
  };
}

function unitSuffix(unit: string | null): string {
  return unit && unit !== 'beds' ? ` ${unit}` : '';
}

function dateOnly(v: string | null): string | null {
  return v ? v.slice(0, 10) : null;
}

function signedPct(v: number | null): string {
  if (v === null) return '—';
  const p = v * 100;
  return `${p >= 0 ? '+' : ''}${p.toFixed(0)}%`;
}

function fmtInt(v: number | null): string {
  return v === null ? '—' : Math.round(v).toLocaleString('en-GB');
}
